import { useEffect, useState } from "react";
import { getRoom, getTickets, getPayments, getCautions } from "../../firebase/services";
import { Badge, Spinner } from "../../components/UI";
import IamLogo from "../../components/IamLogo";

const LOYER = 27500;

const CAUTION_STATUS = {
  pending:              { label: "Non payée ⚠️",          type: "late",  color: "#a32d2d", bg: "#fdecea" },
  paid:                 { label: "Payée ✓",               type: "ok",    color: "#2d6a0a", bg: "#e8f5e0" },
  returned:             { label: "Remboursée 💰",          type: "info",  color: "#1a3a5c", bg: "#e3f1fb" },
  partial:              { label: "Partielle ⚠️",           type: "warn",  color: "#8a4c00", bg: "#fff3e0" },
  pending_confirmation: { label: "En attente confirmation",type: "pend",  color: "#8a4c00", bg: "#fff9e6" },
};

export default function StudentHome({ roomId, onNavigate }) {
  const [room, setRoom]         = useState(null);
  const [tickets, setTickets]   = useState([]);
  const [payments, setPayments] = useState([]);
  const [caution, setCaution]   = useState(null);
  const [loading, setLoading]   = useState(true);

  useEffect(() => {
    if (!roomId) { setLoading(false); return; }
    getRoom(roomId).then(r => { setRoom(r); setLoading(false); });
    const unsubs = [
      getTickets(setTickets, roomId),
      getPayments(setPayments, roomId),
      getCautions(data => setCaution(data[0] || null), roomId),
    ];
    return () => unsubs.forEach(u => u && u());
  }, [roomId]);

  const activeTickets  = tickets.filter(t => t.status !== "done");
  const pendingPayment = payments.find(p => p.status === "pending");

  const payStatus = () => {
    if (room?.paymentStatus === "paid") return { type: "ok",   label: "Payé ce mois ✓" };
    if (pendingPayment)                 return { type: "pend", label: "En attente de confirmation" };
    if (room?.paymentStatus === "late") return { type: "late", label: "En retard" };
    return { type: "warn", label: "À payer" };
  };

  const cautionInfo = caution
    ? (CAUTION_STATUS[caution.status] || CAUTION_STATUS.pending)
    : CAUTION_STATUS.pending;

  if (loading) return (
    <div style={{ display:"flex", flexDirection:"column", height:"100%", background:"#f5f8fc" }}>
      <div style={S.header}><IamLogo height={32} /></div>
      <Spinner />
    </div>
  );

  const ps = payStatus();

  return (
    <div style={S.page}>
      <div style={S.header}>
        <IamLogo height={32} />
        <a
          href="https://wa.me/22377727771?text=Bonjour%20IAM%20Business%20Group%2C%20je%20suis%20locataire%20de%20la%20r%C3%A9sidence."
          target="_blank" rel="noreferrer"
          style={{ background: "#25D366", borderRadius: 20, padding: "6px 12px", textDecoration: "none", fontSize: 12, fontWeight: 700, color: "white", marginRight: 8, display: "flex", alignItems: "center", gap: 5, fontFamily: "'Barlow', sans-serif" }}
          aria-label="Contacter IAM sur WhatsApp"
        >📱 WhatsApp</a>
        <button style={S.bellBtn} onClick={() => onNavigate("chat")} aria-label="Messages">
          <span style={{ fontSize:20 }}>💬</span>
        </button>
      </div>

      <div style={S.scroll}>
        {/* Bienvenue */}
        <div style={S.welcomeCard}>
          <p style={S.welcomeSub}>Bienvenue,</p>
          <p style={S.welcomeName}>{room?.tenantName || "Étudiant"}</p>
          <p style={S.welcomeRoom}>Chambre {room?.number}</p>
        </div>

        {/* Loyer mensuel */}
        <div style={{ ...S.payCard, borderColor: ps.type==="ok"?"#a8d880":ps.type==="late"?"#f5b8b8":"#f5d8a0" }}>
          <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start" }}>
            <div>
              <p style={S.payLabel}>Loyer mensuel</p>
              <p style={S.payAmount}>{LOYER.toLocaleString("fr-FR")} FCFA</p>
              <p style={S.payDue}>Échéance : le {room?.dueDay || 10} de chaque mois</p>
            </div>
            <Badge type={ps.type}>{ps.label}</Badge>
          </div>
          {ps.type !== "ok" && !pendingPayment && (
            <button style={S.payNowBtn} onClick={() => onNavigate("payConfirm")}>
              💳 Déclarer mon paiement
            </button>
          )}
          {pendingPayment && (
            <div style={S.pendingNote}>⏳ Paiement déclaré, en attente de confirmation IAM</div>
          )}
        </div>

        {/* ── CAUTION DE GARANTIE ── */}
        <div style={{ ...S.cautionCard, background: cautionInfo.bg, borderColor: cautionInfo.color + "44" }}>
          <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start" }}>
            <div>
              <p style={S.cautionLabel}>Caution de garantie</p>
              <p style={{ ...S.cautionAmount, color: cautionInfo.color }}>
                {LOYER.toLocaleString("fr-FR")} FCFA
              </p>
              <p style={S.cautionSub}>1 mois de loyer · remboursée à votre départ</p>
            </div>
            <div style={{ ...S.cautionBadge, background: cautionInfo.color, color:"white" }}>
              {cautionInfo.label}
            </div>
          </div>

          {/* Message selon statut */}
          {(!caution || caution.status === "pending") && (
            <div style={S.cautionAlert}>
              ⚠️ Votre caution de garantie n'a pas encore été enregistrée. Contactez IAM pour régulariser.
            </div>
          )}
          {caution?.status === "paid" && caution?.note && (
            <div style={S.cautionNote}>📝 {caution.note}</div>
          )}
          {caution?.status === "returned" && (
            <div style={{ ...S.cautionAlert, background:"#e3f1fb", color:"#1a3a5c" }}>
              💰 Votre caution a été remboursée par IAM Business Group.
            </div>
          )}
          {caution?.status === "pending_confirmation" && (
            <div style={{ ...S.cautionAlert, background:"#fff9e6", color:"#8a6a00" }}>
              ⏳ Paiement de caution déclaré — en attente de confirmation IAM.
            </div>
          )}
        </div>

        {/* Tickets actifs */}
        {activeTickets.length > 0 && (
          <div style={S.ticketAlert}>
            <span style={{ fontSize:16 }}>🔧</span>
            <div style={{ flex:1 }}>
              <p style={S.ticketAlertTitle}>{activeTickets.length} ticket{activeTickets.length>1?"s":""} en cours</p>
              <p style={S.ticketAlertSub}>{activeTickets[0]?.category} — {activeTickets[0]?.status==="prog"?"En cours":"En attente"}</p>
            </div>
            <button style={S.ticketAlertBtn} onClick={() => onNavigate("tickets")}>Voir</button>
          </div>
        )}

        {/* Actions rapides */}
        <p style={S.sectionTitle}>Actions rapides</p>
        <div style={S.actionsGrid}>
          <button style={S.actionCard} onClick={() => onNavigate("newTicket")}>
            <span style={{ fontSize:26 }}>🔧</span>
            <span style={S.actionLabel}>Signaler un problème</span>
          </button>
          <button style={S.actionCard} onClick={() => onNavigate("payConfirm")}>
            <span style={{ fontSize:26 }}>💳</span>
            <span style={S.actionLabel}>Déclarer paiement</span>
          </button>
          <button style={S.actionCard} onClick={() => onNavigate("chat")}>
            <span style={{ fontSize:26 }}>💬</span>
            <span style={S.actionLabel}>Contacter IAM</span>
          </button>
          <button style={S.actionCard} onClick={() => onNavigate("history")}>
            <span style={{ fontSize:26 }}>📋</span>
            <span style={S.actionLabel}>Historique</span>
          </button>
        </div>
      </div>
    </div>
  );
}

const S = {
  page:    { display:"flex", flexDirection:"column", height:"100%", background:"#f5f8fc", fontFamily:"'Barlow',sans-serif" },
  header:  { background:"#1a3a5c", padding:"12px 16px", display:"flex", alignItems:"center", justifyContent:"space-between" },
  bellBtn: { background:"none", border:"none", cursor:"pointer", padding:4 },
  scroll:  { flex:1, overflowY:"auto", paddingBottom:80 },
  welcomeCard: { background:"linear-gradient(135deg,#1a3a5c,#2e6aa0)", margin:16, borderRadius:16, padding:"20px 18px" },
  welcomeSub:  { fontSize:12, color:"#90bde0", margin:"0 0 2px" },
  welcomeName: { fontSize:22, fontWeight:800, color:"white", margin:"0 0 2px", fontFamily:"'Barlow Condensed',sans-serif" },
  welcomeRoom: { fontSize:13, color:"#90bde0", margin:0 },
  payCard:    { margin:"0 16px 12px", background:"white", borderRadius:16, padding:"16px", border:"1.5px solid #d0dce8" },
  payLabel:   { fontSize:12, color:"#7a90a8", margin:"0 0 2px" },
  payAmount:  { fontSize:26, fontWeight:800, color:"#1a3a5c", margin:"0 0 2px", fontFamily:"'Barlow Condensed',sans-serif" },
  payDue:     { fontSize:12, color:"#7a90a8", margin:0 },
  payNowBtn:  { width:"100%", marginTop:12, padding:"11px", borderRadius:10, background:"#1a3a5c", color:"white", border:"none", fontSize:14, fontWeight:600, cursor:"pointer", fontFamily:"inherit" },
  pendingNote:{ marginTop:10, padding:"8px 12px", background:"#fff9e6", borderRadius:8, fontSize:12, color:"#8a6a00" },
  // Caution card
  cautionCard:  { margin:"0 16px 12px", borderRadius:16, padding:"14px 16px", border:"1.5px solid" },
  cautionLabel: { fontSize:12, color:"#7a90a8", margin:"0 0 2px" },
  cautionAmount:{ fontSize:22, fontWeight:800, margin:"0 0 2px", fontFamily:"'Barlow Condensed',sans-serif" },
  cautionSub:   { fontSize:11, color:"#7a90a8", margin:0 },
  cautionBadge: { fontSize:10, fontWeight:700, borderRadius:20, padding:"3px 10px", whiteSpace:"nowrap", flexShrink:0 },
  cautionAlert: { marginTop:10, padding:"8px 10px", background:"#fdecea", borderRadius:8, fontSize:12, color:"#a32d2d", lineHeight:1.5 },
  cautionNote:  { marginTop:8, fontSize:12, color:"#5a7a9a", fontStyle:"italic" },
  // Tickets
  ticketAlert:     { margin:"0 16px 12px", background:"white", borderRadius:12, padding:"12px 14px", display:"flex", alignItems:"center", gap:10, border:"1.5px solid #f5d8a0" },
  ticketAlertTitle:{ fontSize:13, fontWeight:600, color:"#1a2a3a", margin:0 },
  ticketAlertSub:  { fontSize:11, color:"#7a90a8", margin:0 },
  ticketAlertBtn:  { padding:"6px 12px", background:"#fff3e0", color:"#8a4c00", border:"none", borderRadius:8, fontSize:12, fontWeight:600, cursor:"pointer", fontFamily:"inherit" },
  sectionTitle: { fontSize:12, textTransform:"uppercase", letterSpacing:"0.1em", color:"#7a90a8", padding:"4px 16px 8px", fontWeight:600 },
  actionsGrid:  { display:"grid", gridTemplateColumns:"1fr 1fr", gap:10, padding:"0 16px" },
  actionCard:   { background:"white", borderRadius:14, padding:"16px 14px", border:"0.5px solid #d0dce8", display:"flex", flexDirection:"column", alignItems:"center", gap:8, cursor:"pointer", fontFamily:"inherit" },
  actionLabel:  { fontSize:12, fontWeight:600, color:"#2a4a6a", textAlign:"center" },
};
